import React, { useState, useEffect } from "react";
import {
  Container,
  Pagination,
} from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import "./index.scss";
import { RepairShop, RepairShopFormData, Service } from "./types";
import AppConstants from "../../AppConstants";

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const RepairShopManagement: React.FC = () => {
  const [shops, setShops] = useState<RepairShop[]>([]);
  const [services, setServices] = useState<Array<{_id: string, name: string}>>([]);
  const [states, setStates] = useState<Array<{_id: string, name: string, abbreviation: string}>>([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentShop, setCurrentShop] = useState<RepairShop | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shopToDelete, setShopToDelete] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [formData, setFormData] = useState<RepairShopFormData>({
    businessName: "",
    contactPerson: "",
    phoneNumber: "",
    email: "",
    website: "",
    street: "",
    city: "",
    state: "",
    stateName: "",
    zipCode: "",
    services: [],
    businessHours: "",
    subscriptionPlan: "",
    monthlyFee: "",
    subscriptionStartDate: "",
    status: "pending",
    notes: "",
  });

  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [touchedFields, setTouchedFields] = useState<{[key: string]: boolean}>({});

  const [selectedFiles, setSelectedFiles] = useState<{
    businessLicense: File | null;
    insuranceCertificate: File | null;
    logo: File | null;
  }>({
    businessLicense: null,
    insuranceCertificate: null,
    logo: null,
  });

  // State for existing file URLs when editing
  const [existingFileUrls, setExistingFileUrls] = useState<{
    businessLicense?: string;
    insuranceCertificate?: string;
    logo?: string;
  }>({});

  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    pending: 0,
  });

  // Fetch states from API
  const fetchStates = async () => {
    try {
      const response = await axios.get(AppConstants.ADMIN_STATES_API);
      if (response.data.success) {
        setStates(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching states:', error);
      toast.error('Failed to load states');
    }
  };

  // Fetch repair shops from API
  const fetchRepairShops = async (page: number = 1, search: string = '') => {
    try {
      setDataLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please login again');
        return;
      }

      let url = `${AppConstants.ADMIN_REPAIR_SHOPS_API}?page=${page}&limit=${itemsPerPage}`;
      
      // Add search parameter if provided
      if (search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Handle the paginated response structure
        const responseData = response.data.data;
        
        if (responseData && Array.isArray(responseData.repairShops)) {
          setShops(responseData.repairShops);
          
          // Set pagination data
          if (responseData.pagination) {
            setPagination({
              currentPage: responseData.pagination.currentPage,
              totalPages: responseData.pagination.totalPages,
              totalCount: responseData.pagination.totalCount,
              hasNext: responseData.pagination.hasNext,
              hasPrev: responseData.pagination.hasPrev
            });
          }

          // Extract stats if available
          if (responseData.stats) {
            setStats({
              total: responseData.stats.total || 0,
              verified: responseData.stats.verified || 0,
              pending: responseData.stats.pending || 0
            });
          }
        } else {
          setShops([]);
          setPagination(null);
        }
      } else {
        setShops([]);
        setPagination(null);
      }
    } catch (error: any) {
      console.error('Error fetching repair shops:', error);
      setShops([]); // Ensure shops is always an array
      setPagination(null);
      toast.error('Failed to load repair shops');
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch services from API
  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please login again');
        return;
      }

      const response = await axios.get(AppConstants.ADMIN_SERVICES_API, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Handle both old and new API response formats
        const servicesData = response.data.data?.services || response.data.data || [];
        setServices(Array.isArray(servicesData) ? servicesData : []);
      }
    } catch (error: any) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    if (!pagination) return null;

    const items = [];
    const totalPages = pagination.totalPages || 1;
    const current = pagination.currentPage || 1;

    // First page
    items.push(
      <Pagination.First
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={current === 1}
      />
    );

    // Previous page
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(Math.max(1, current - 1))}
        disabled={current === 1}
      />
    );

    // Page numbers (show current page and 2 pages before/after)
    const startPage = Math.max(1, current - 2);
    const endPage = Math.min(totalPages, current + 2);

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === current}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    // Next page
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, current + 1))}
        disabled={current === totalPages}
      />
    );

    // Last page
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={current === totalPages}
      />
    );

    return items;
  };

  useEffect(() => {
    // Fetch data on component mount
    fetchServices();
    fetchStates();
    fetchRepairShops(currentPage, searchQuery);
  }, [currentPage]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
      fetchRepairShops(1, searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleOpenModal = (shop?: RepairShop) => {
    if (shop) {
      setIsEditMode(true);
      setCurrentShop(shop);

      // Extract services - handle both old format (IDs) and new format (offerings)
      const serviceOfferings = shop.services.map((service: any) => {
        if (typeof service === 'string') {
          // Old format: just ID
          return { service, estimatedCost: '', partsCost: '', estimatedTime: '', estimatedTimeUnit: 'Minutes' as const };
        } else if (service.service) {
          // New format: offering object with populated service
          const serviceId = typeof service.service === 'string' ? service.service : service.service._id;
          return {
            service: serviceId,
            estimatedCost: service.estimatedCost?.toString() || '',
            partsCost: service.partsCost?.toString() || '',
            estimatedTime: service.estimatedTime?.toString() || '',
            estimatedTimeUnit: service.estimatedTimeUnit || 'Minutes' as const
          };
        } else {
          // Old populated format: {_id, name, ...}
          return { service: service._id, estimatedCost: '', partsCost: '', estimatedTime: '', estimatedTimeUnit: 'Minutes' as const };
        }
      });

      setFormData({
        businessName: shop.businessName,
        contactPerson: shop.contactPerson || "",
        phoneNumber: shop.phoneNumber, // Keep original format
        email: shop.email,
        website: shop.website || "",
        street: shop.address.street,
        city: shop.address.city,
        state: shop.address.state,
        stateName: shop.address.stateName || "",
        zipCode: shop.address.zipCode,
        services: serviceOfferings,
        businessHours: shop.businessHours || "",
        subscriptionPlan: shop.subscriptionPlan,
        monthlyFee: shop.monthlyFee?.toString() || "",
        subscriptionStartDate: shop.subscriptionStartDate ?
          new Date(shop.subscriptionStartDate).toISOString().split('T')[0] : "",
        status: shop.status,
        notes: shop.notes || "",
      });
      
      // Set existing file URLs
      setExistingFileUrls({
        businessLicense: shop.businessLicense,
        insuranceCertificate: shop.insuranceCertificate,
        logo: shop.logo
      });
      
      // Clear selected files when editing (user can choose to upload new ones)
      setSelectedFiles({
        businessLicense: null,
        insuranceCertificate: null,
        logo: null
      });
    } else {
      setIsEditMode(false);
      setCurrentShop(null);
      setFormData({
        businessName: "",
        contactPerson: "",
        phoneNumber: "",
        email: "",
        website: "",
        street: "",
        city: "",
        state: "",
        stateName: "",
        zipCode: "",
        services: [],
        businessHours: "",
        subscriptionPlan: "",
        monthlyFee: "",
        subscriptionStartDate: "",
        status: "pending",
        notes: "",
      });
      
      // Clear existing file URLs
      setExistingFileUrls({});
    }
    setFormErrors({});
    setTouchedFields({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setCurrentShop(null);
    setFormErrors({});
    setTouchedFields({});
    setSelectedFiles({
      businessLicense: null,
      insuranceCertificate: null,
      logo: null,
    });
    setExistingFileUrls({});
  };

  // Helper function to get file display text
  const getFileDisplayText = (fileType: 'businessLicense' | 'insuranceCertificate' | 'logo') => {
    const selectedFile = selectedFiles[fileType];
    const existingUrl = existingFileUrls[fileType];
    
    if (selectedFile) {
      return `✓ ${selectedFile.name} (new file selected)`;
    } else if (existingUrl) {
      const fileName = existingUrl.split('/').pop() || 'existing file';
      return `📎 ${fileName} (current file)`;
    } else {
      const defaultTexts = {
        businessLicense: "Click to upload business license",
        insuranceCertificate: "Click to upload insurance certificate", 
        logo: "Click to upload logo"
      };
      return defaultTexts[fileType];
    }
  };

  // Helper function to check if a file exists (either selected or existing)
  const hasFile = (fileType: 'businessLicense' | 'insuranceCertificate' | 'logo') => {
    return selectedFiles[fileType] || existingFileUrls[fileType];
  };

  // Validation functions
  const validateField = (name: string, value: string) => {
    let error = "";

    switch (name) {
      case "businessName":
        if (!value.trim()) {
          error = "Business name is required";
        } else if (value.trim().length < 2) {
          error = "Business name must be at least 2 characters";
        }
        break;
      case "phoneNumber":
        if (!value.trim()) {
          error = "Phone number is required";
        } else {
          // Support multiple phone formats to match backend
          const phonePatterns = [
            /^\(\d{3}\) \d{3}-\d{4}$/, // (555) 123-4567
            /^\d{3}-\d{3}-\d{4}$/, // 555-123-4567
            /^\d{10}$/, // 5551234567
          ];
          const isValid = phonePatterns.some(pattern => pattern.test(value));
          if (!isValid) {
            error = "Phone number must be in format (555) 123-4567, 555-123-4567, or 5551234567";
          }
        }
        break;
      case "email":
        if (!value.trim()) {
          error = "Email address is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "street":
        if (!value.trim()) {
          error = "Street address is required";
        }
        break;
      case "city":
        if (!value.trim()) {
          error = "City is required";
        }
        break;
      case "state":
        if (!value.trim()) {
          error = "State is required";
        }
        break;
      case "zipCode":
        if (!value.trim()) {
          error = "ZIP code is required";
        } else if (!/^\d{5}(-\d{4})?$/.test(value)) {
          error = "ZIP code must be 5 digits or 5+4 format";
        }
        break;
      case "subscriptionPlan":
        if (!value.trim()) {
          error = "Subscription plan is required";
        }
        break;
      case "services":
        // This will be handled separately since it's an array
        break;
    }

    return error;
  };

  const formatPhoneNumber = (digits: string) => {
    // Only work with digits
    const phoneNumber = digits.replace(/\D/g, '');
    
    // Return empty if no digits
    if (phoneNumber.length === 0) return '';
    
    // Format based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    } else {
      // Limit to 10 digits
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Special handling for phone number
    if (name === "phoneNumber") {
      // Get only digits from the input
      const digits = value.replace(/\D/g, '');
      
      // If completely empty, keep it empty
      if (digits.length === 0) {
        formattedValue = '';
      } else {
        // Format based on number of digits
        formattedValue = formatPhoneNumber(digits);
      }
    }

    // Special handling for subscription plan to auto-populate monthly fee
    if (name === 'subscriptionPlan') {
      let monthlyFee = '';
      if (value === 'basic') {
        monthlyFee = '89.99';
      } else if (value === 'verified') {
        monthlyFee = '119.99';
      }
      
      setFormData((prev) => ({
        ...prev,
        subscriptionPlan: formattedValue,
        monthlyFee: monthlyFee,
      }));
    }
    // Special handling for state selection to store both abbreviation and full name
    else if (name === 'state') {
      const selectedState = states.find(state => state.abbreviation === value);
      setFormData((prev) => ({
        ...prev,
        state: formattedValue, // abbreviation
        stateName: selectedState?.name || '' // full name
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    }

    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate field and update errors
    const error = validateField(name, formattedValue);
    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setTouchedFields((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    
    // If backspace or delete and field is empty, allow it
    if ((e.keyCode === 8 || e.keyCode === 46) && e.currentTarget.value === '') {
      return;
    }
  };

  const handleServiceToggle = (serviceId: string) => {
    setFormData((prev) => {
      const isSelected = prev.services.some(s => s.service === serviceId);
      if (isSelected) {
        return {
          ...prev,
          services: prev.services.filter((s) => s.service !== serviceId)
        };
      } else {
        return {
          ...prev,
          services: [...prev.services, { service: serviceId, estimatedCost: '', partsCost: '', estimatedTime: '', estimatedTimeUnit: 'Minutes' }]
        };
      }
    });
  };

  const handleServiceCostChange = (serviceId: string, cost: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map(s =>
        s.service === serviceId ? { ...s, estimatedCost: cost } : s
      )
    }));
  };

  const handlePartsCostChange = (serviceId: string, cost: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map(s =>
        s.service === serviceId ? { ...s, partsCost: cost } : s
      )
    }));
  };

  const handleServiceTimeChange = (serviceId: string, time: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map(s =>
        s.service === serviceId ? { ...s, estimatedTime: time } : s
      )
    }));
  };

  const handleServiceTimeUnitChange = (serviceId: string, unit: 'Minutes' | 'Hours') => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map(s =>
        s.service === serviceId ? { ...s, estimatedTimeUnit: unit } : s
      )
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: string) => {
    console.log(`=== FILE CHANGE DEBUG: ${fileType} ===`);
    console.log('Event target files:', e.target.files);
    
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log('Selected file:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      setSelectedFiles((prev) => {
        const updated = {
          ...prev,
          [fileType]: file,
        };
        console.log('Updated selectedFiles:', updated);
        return updated;
      });
    } else {
      console.log('No file selected or files array empty');
    }
  };

  const handleSaveShop = async () => {
    // Validate all required fields
    const requiredFields = ["businessName", "phoneNumber", "email", "street", "city", "state", "stateName", "zipCode", "subscriptionPlan"];
    const newErrors: {[key: string]: string} = {};
    let hasErrors = false;

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field as keyof RepairShopFormData] as string);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    // Validate services
    if (formData.services.length === 0) {
      newErrors.services = "At least one service must be selected";
      hasErrors = true;
    }

    // Mark all required fields as touched
    const newTouched: {[key: string]: boolean} = {};
    requiredFields.forEach(field => {
      newTouched[field] = true;
    });
    newTouched.services = true;

    setFormErrors(newErrors);
    setTouchedFields(newTouched);

    if (hasErrors) {
      toast.error("Please fix the validation errors before submitting");
      return;
    }

    setLoading(true);

    try {
      // Prepare the FormData for file upload
      const formDataPayload = new FormData();
      
      // Add text fields
      formDataPayload.append('businessName', formData.businessName.trim());
      if (formData.contactPerson.trim()) {
        formDataPayload.append('contactPerson', formData.contactPerson.trim());
      }
      formDataPayload.append('phoneNumber', formData.phoneNumber);
      formDataPayload.append('email', formData.email.toLowerCase().trim());
      if (formData.website.trim()) {
        formDataPayload.append('website', formData.website.trim());
      }
      
      // Add address as nested object
      formDataPayload.append('address[street]', formData.street.trim());
      formDataPayload.append('address[city]', formData.city.trim());
      formDataPayload.append('address[state]', formData.state.toUpperCase());
      formDataPayload.append('address[stateName]', formData.stateName.trim());
      formDataPayload.append('address[zipCode]', formData.zipCode.trim());
      
      // Add services array with cost, parts cost, time, and time unit
      formData.services.forEach((offering, index) => {
        formDataPayload.append(`services[${index}][service]`, offering.service);
        if (offering.estimatedCost) {
          formDataPayload.append(`services[${index}][estimatedCost]`, offering.estimatedCost);
        }
        if (offering.partsCost) {
          formDataPayload.append(`services[${index}][partsCost]`, offering.partsCost);
        }
        if (offering.estimatedTime) {
          formDataPayload.append(`services[${index}][estimatedTime]`, offering.estimatedTime);
        }
        if (offering.estimatedTimeUnit) {
          formDataPayload.append(`services[${index}][estimatedTimeUnit]`, offering.estimatedTimeUnit);
        }
      });
      
      if (formData.businessHours.trim()) {
        formDataPayload.append('businessHours', formData.businessHours.trim());
      }
      formDataPayload.append('subscriptionPlan', formData.subscriptionPlan);
      if (formData.monthlyFee) {
        formDataPayload.append('monthlyFee', formData.monthlyFee);
      }
      if (formData.subscriptionStartDate) {
        formDataPayload.append('subscriptionStartDate', formData.subscriptionStartDate);
      }
      formDataPayload.append('status', formData.status);
      if (formData.notes.trim()) {
        formDataPayload.append('notes', formData.notes.trim());
      }
      
      // Add files
      console.log('=== FRONTEND FILE UPLOAD DEBUG ===');
      console.log('selectedFiles:', selectedFiles);
      
      if (selectedFiles.businessLicense) {
        console.log('Adding business license file:', selectedFiles.businessLicense.name);
        formDataPayload.append('businessLicense', selectedFiles.businessLicense);
      } else {
        console.log('No business license file selected');
      }
      
      if (selectedFiles.insuranceCertificate) {
        console.log('Adding insurance certificate file:', selectedFiles.insuranceCertificate.name);
        formDataPayload.append('insuranceCertificate', selectedFiles.insuranceCertificate);
      } else {
        console.log('No insurance certificate file selected');
      }
      
      if (selectedFiles.logo) {
        console.log('Adding logo file:', selectedFiles.logo.name);
        formDataPayload.append('logo', selectedFiles.logo);
      } else {
        console.log('No logo file selected');
      }

      // Debug: Log FormData contents
      console.log('FormData contents:');
      console.log('FormData has entries:', formDataPayload.has('repairShop'));
      console.log('FormData has profileImage:', formDataPayload.has('profileImage'));
      console.log('FormData has logo:', formDataPayload.has('logo'));

      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData - browser sets it with boundary
        },
      };

      if (isEditMode && currentShop) {
        // Update existing repair shop
        const response = await axios.put(
          `${AppConstants.ADMIN_UPDATE_REPAIR_SHOP_API}/${currentShop._id}`,
          formDataPayload,
          config
        );

        if (response.data.success) {
          // Refresh repair shops list
          await fetchRepairShops(currentPage, searchQuery);
          toast.success("✓ Repair shop updated successfully!");
        }
      } else {
        // Create new repair shop
        const response = await axios.post(
          AppConstants.ADMIN_CREATE_REPAIR_SHOP_API,
          formDataPayload,
          config
        );

        if (response.data.success) {
          // Refresh repair shops list
          await fetchRepairShops(currentPage, searchQuery);
          toast.success("✓ Repair shop created successfully!\n\nThe shop will now appear in the driver app.");
        }
      }

      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving repair shop:', error);
      
      if (error.response?.data?.message) {
        toast.error(`Error: ${error.response.data.message}`);
      } else if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 400) {
        toast.error('Invalid data provided. Please check your inputs.');
      } else if (error.message) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error('Failed to save repair shop. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (id: string) => {
    setShopToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setShopToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!shopToDelete) return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please login again');
        return;
      }

      await axios.put(`${AppConstants.ADMIN_DELETE_REPAIR_SHOP_API}/${shopToDelete}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Refresh repair shops list
      await fetchRepairShops(currentPage, searchQuery);
      toast.success("Repair shop deleted successfully");
      setShowDeleteModal(false);
      setShopToDelete(null);
    } catch (error: any) {
      console.error('Error deleting repair shop:', error);
      toast.error('Failed to delete repair shop');
    }
  };

  const handleExportRepairShops = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please login again');
        return;
      }

      // Make API call to download Excel file
      const response = await axios.get(AppConstants.ADMIN_EXPORT_REPAIR_SHOPS_API, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob', // Important for file download
        params: {
          search: searchQuery || undefined, // Include current search filter if any
        }
      });

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create a link element and trigger download
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = `repair_shops_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Repair shops exported successfully!');
    } catch (error: any) {
      console.error('Error exporting repair shops:', error);
      if (error.response?.status === 404) {
        toast.warning('No repair shops found to export');
      } else {
        toast.error('Failed to export repair shops');
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Error display component
  const ErrorMessage: React.FC<{ field: string }> = ({ field }) => {
    if (!touchedFields[field] || !formErrors[field]) return null;
    return <div className="field-error">{formErrors[field]}</div>;
  };

  return (
    <div className="repair-shop-management">
      <Container fluid>
        {/* Top Bar */}
        <div className="top-bar">
          <div>
            <div className="page-title">Repair Shop Management</div>
            <div className="page-subtitle">Manage and verify repair shop partners</div>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon green">🔧</div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Repair Shops</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon blue">✓</div>
            <div className="stat-value">{stats.verified}</div>
            <div className="stat-label">Verified Partners</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon orange">⏳</div>
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending Review</div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              className="btn btn-secondary"
              onClick={handleExportRepairShops}
              disabled={exportLoading}
            >
              <span>{exportLoading ? '⏳' : '📥'}</span>
              <span>{exportLoading ? 'Exporting...' : 'Export'}</span>
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <span>+</span>
            <span>Add New Repair Shop</span>
          </button>
        </div>

        {/* Data Table */}
        <div className="data-table">
          <div className="table-header">
            <div className="table-title">All Repair Shops</div>
            <div className="search-box">
              <span>🔍</span>
              <input
                type="text"
                placeholder="Search by name, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Business Name</th>
                  <th>Location</th>
                  <th>Services</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dataLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center">
                      <div className="d-flex justify-content-center align-items-center" style={{ padding: "2rem" }}>
                        <div className="spinner-border text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading repair shops...
                      </div>
                    </td>
                  </tr>
                ) : shops.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center" style={{ padding: "2rem" }}>
                      {searchQuery ? `No repair shops found matching "${searchQuery}"` : "No repair shops found"}
                    </td>
                  </tr>
                ) : (
                  shops.map((shop:any) => (
                  <tr key={shop._id || shop.id}>
                    <td>
                      <div className="shop-name-cell">
                        <div className="shop-logo">🔧</div>
                        <div className="shop-name">{shop.businessName}</div>
                      </div>
                    </td>
                    <td>{shop.location || (shop.address ? `${shop.address.city}, ${shop.address.state}` : '')}</td>
                    <td>
                      {Array.isArray(shop.services)
                        ? shop.services.map((service: any) => {
                            if (typeof service === 'string') {
                              return service;
                            } else if (service.service) {
                              // New format: {service: {name: "..."}, estimatedCost, ...}
                              return typeof service.service === 'string'
                                ? service.service
                                : service.service.name;
                            } else {
                              // Old format: {name: "..."}
                              return service.name;
                            }
                          }).join(", ")
                        : ""
                      }
                    </td>
                    <td>
                      <span className={`status-${shop.status}`}>
                        ● {shop.status?.charAt(0).toUpperCase() + shop.status?.slice(1)}
                      </span>
                    </td>
                    <td>
                      {shop.isVerified ? (
                        <span className="verified-badge">✓ Verified</span>
                      ) : (
                        "Regular"
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="action-icon-btn edit"
                          onClick={() => handleOpenModal(shop)}
                        >
                          ✏️
                        </button>
                        <button
                          className="action-icon-btn delete"
                          onClick={() => handleOpenDeleteModal(shop._id || shop.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalCount > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3 pagination-block">
              <div>
                Showing: {((pagination.currentPage - 1) * itemsPerPage) + 1} to {Math.min(
                  pagination.currentPage * itemsPerPage,
                  pagination.totalCount
                )} of {pagination.totalCount} repair shops
              </div>
              <Pagination className="mb-0">
                {renderPaginationItems()}
              </Pagination>
            </div>
          )}
        </div>
      </Container>

      {/* Add/Edit Modal - Exact HTML Structure */}
      <div className={`modal ${showModal ? 'active' : ''}`} onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
        <div className="modal-content">
          <div className="modal-header">
            <div className="modal-title">
              {isEditMode ? "Edit Repair Shop" : "Add New Repair Shop"}
            </div>
            <button className="close-btn" onClick={handleCloseModal}>×</button>
          </div>
          <div className="modal-body">
            <form>
              {/* Basic Info */}
              <div className="form-section">
                <div className="section-heading">
                  <span>📋</span>
                  <span>Basic Information</span>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      Business Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${touchedFields.businessName && formErrors.businessName ? 'error' : ''}`}
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter business name"
                      required
                    />
                    <ErrorMessage field="businessName" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Person</label>
                    <input
                      type="text"
                      className="form-input"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      placeholder="Enter contact name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`form-input ${touchedFields.phoneNumber && formErrors.phoneNumber ? 'error' : ''}`}
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      onKeyDown={handlePhoneKeyDown}
                      placeholder="Enter Phone Number"
                      maxLength={14}
                      required
                    />
                    <ErrorMessage field="phoneNumber" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-input ${touchedFields.email && formErrors.email ? 'error' : ''}`}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter email address"
                      required
                    />
                    <ErrorMessage field="email" />
                  </div>
                  <div className="form-group full-width">
                    <label className="form-label">Website URL</label>
                    <input
                      type="url"
                      className="form-input"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="form-section">
                <div className="section-heading">
                  <span>📍</span>
                  <span>Location Details</span>
                </div>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label className="form-label">
                      Street Address <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${touchedFields.street && formErrors.street ? 'error' : ''}`}
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter street address"
                      required
                    />
                    <ErrorMessage field="street" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${touchedFields.city && formErrors.city ? 'error' : ''}`}
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter city"
                      required
                    />
                    <ErrorMessage field="city" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      State <span className="required">*</span>
                    </label>
                    <select
                      className={`form-select ${touchedFields.state && formErrors.state ? 'error' : ''}`}
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state._id} value={state.abbreviation}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage field="state" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      ZIP Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${touchedFields.zipCode && formErrors.zipCode ? 'error' : ''}`}
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter ZIP code"
                      maxLength={10}
                      required
                    />
                    <ErrorMessage field="zipCode" />
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="form-section">
                <div className="section-heading">
                  <span>🔧</span>
                  <span>Services Offered</span>
                </div>
                <div className="services-grid">
                  {Array.isArray(services) && services.map((service) => {
                    const isSelected = formData.services.some(s => s.service === service._id);
                    const selectedService = formData.services.find(s => s.service === service._id);

                    return (
                      <div className="service-item" key={service._id}>
                        <div className="service-checkbox">
                          <input
                            type="checkbox"
                            id={`service-${service._id}`}
                            checked={isSelected}
                            onChange={() => handleServiceToggle(service._id)}
                          />
                          <label htmlFor={`service-${service._id}`}>{service.name}</label>
                        </div>
                        {isSelected && (
                          <div className="service-details">
                            <div className="service-row">
                              <div className="service-input-group">
                                <label className="service-input-label">Labor Cost</label>
                                <input
                                  type="text"
                                  className="service-input"
                                  placeholder="10,000"
                                  value={selectedService?.estimatedCost || ''}
                                  onChange={(e) => handleServiceCostChange(service._id, e.target.value)}
                                />
                              </div>
                              <div className="service-input-group">
                                <label className="service-input-label">Parts Cost</label>
                                <input
                                  type="text"
                                  className="service-input"
                                  placeholder="5,000"
                                  value={selectedService?.partsCost || ''}
                                  onChange={(e) => handlePartsCostChange(service._id, e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="service-row">
                              <div className="service-input-group time-group">
                                <label className="service-input-label">Est Time</label>
                                <div className="time-input-wrapper">
                                  <input
                                    type="number"
                                    className="service-input time-number-input"
                                    placeholder="5"
                                    min="0"
                                    value={selectedService?.estimatedTime || ''}
                                    onChange={(e) => handleServiceTimeChange(service._id, e.target.value)}
                                  />
                                  <select
                                    className="service-input time-unit-select"
                                    value={selectedService?.estimatedTimeUnit || 'Minutes'}
                                    onChange={(e) => handleServiceTimeUnitChange(service._id, e.target.value as 'Minutes' | 'Hours')}
                                  >
                                    <option value="Minutes">Minutes</option>
                                    <option value="Hours">Hours</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <ErrorMessage field="services" />
              </div>

              {/* Business Hours */}
              <div className="form-section">
                <div className="section-heading">
                  <span>🕐</span>
                  <span>Business Hours</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Operating Hours</label>
                  <textarea
                    className="form-textarea"
                    name="businessHours"
                    value={formData.businessHours}
                    onChange={handleInputChange}
                    placeholder="Enter operating hours"
                  />
                </div>
              </div>

              {/* Subscription */}
              <div className="form-section">
                <div className="section-heading">
                  <span>💰</span>
                  <span>Subscription & Verification</span>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      Subscription Plan <span className="required">*</span>
                    </label>
                    <select
                      className={`form-select ${touchedFields.subscriptionPlan && formErrors.subscriptionPlan ? 'error' : ''}`}
                      name="subscriptionPlan"
                      value={formData.subscriptionPlan}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                    >
                      <option value="">Select plan</option>
                      <option value="basic">Basic</option>
                      <option value="verified">Verified</option>
                    </select>
                    <ErrorMessage field="subscriptionPlan" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Monthly Fee</label>
                    <input
                      type="number"
                      className="form-input"
                      name="monthlyFee"
                      value={formData.monthlyFee}
                      onChange={handleInputChange}
                      placeholder="Enter monthly fee"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subscription Start Date</label>
                    <input
                      type="date"
                      className="form-input"
                      name="subscriptionStartDate"
                      value={formData.subscriptionStartDate}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Status <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="pending">Pending Review</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="form-section">
                <div className="section-heading">
                  <span>📄</span>
                  <span>Documents & Verification</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Business License</label>
                  <div
                    className="file-upload"
                    onClick={() => document.getElementById("businessLicense")?.click()}
                  >
                    <div className="file-upload-icon">📄</div>
                    <div className="file-upload-text">
                      {getFileDisplayText('businessLicense')}
                    </div>
                    {existingFileUrls.businessLicense && (
                      <div className="existing-file-actions">
                        <a 
                          href={existingFileUrls.businessLicense} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-file-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Current File
                        </a>
                      </div>
                    )}
                    <div className="file-upload-hint">PDF, JPG, PNG (Max 5MB)</div>
                    <input
                      type="file"
                      id="businessLicense"
                      style={{ display: "none" }}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, "businessLicense")}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Insurance Certificate</label>
                  <div
                    className="file-upload"
                    onClick={() => document.getElementById("insuranceCertificate")?.click()}
                  >
                    <div className="file-upload-icon">📄</div>
                    <div className="file-upload-text">
                      {getFileDisplayText('insuranceCertificate')}
                    </div>
                    {existingFileUrls.insuranceCertificate && (
                      <div className="existing-file-actions">
                        <a 
                          href={existingFileUrls.insuranceCertificate} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-file-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Current File
                        </a>
                      </div>
                    )}
                    <div className="file-upload-hint">PDF, JPG, PNG (Max 5MB)</div>
                    <input
                      type="file"
                      id="insuranceCertificate"
                      style={{ display: "none" }}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, "insuranceCertificate")}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Business Logo</label>
                  <div
                    className="file-upload"
                    onClick={() => document.getElementById("logo")?.click()}
                  >
                    <div className="file-upload-icon">🖼️</div>
                    <div className="file-upload-text">
                      {getFileDisplayText('logo')}
                    </div>
                    {existingFileUrls.logo && (
                      <div className="existing-file-actions">
                        <a 
                          href={existingFileUrls.logo} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-file-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Current File
                        </a>
                      </div>
                    )}
                    <div className="file-upload-hint">JPG, PNG (Max 2MB)</div>
                    <input
                      type="file"
                      id="logo"
                      style={{ display: "none" }}
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, "logo")}
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="form-section">
                <div className="section-heading">
                  <span>📝</span>
                  <span>Additional Information</span>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Notes / Comments</label>
                  <textarea
                    className="form-textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any additional notes or special requirements..."
                  />
                </div>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-large" onClick={handleCloseModal} disabled={loading}>
              Cancel
            </button>
            <button className="btn btn-primary btn-large" onClick={handleSaveShop} disabled={loading}>
              <span>{loading ? "⏳" : "✓"}</span>
              <span>{loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Repair Shop" : "Save Repair Shop")}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal ${showDeleteModal ? 'active' : ''}`} onClick={(e) => e.target === e.currentTarget && handleCancelDelete()}>
        <div className="modal-content" style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <div className="modal-title">Confirm Delete</div>
            <button className="close-btn" onClick={handleCancelDelete}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
              <p style={{ fontSize: '18px', fontWeight: '600', color: '#2d3436', marginBottom: '12px' }}>
                Are you sure you want to delete this repair shop?
              </p>
              <p style={{ fontSize: '14px', color: '#636e72' }}>
                This action will mark the repair shop as deleted. This cannot be undone.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-large" onClick={handleCancelDelete}>
              Cancel
            </button>
            <button className="btn btn-primary btn-large" onClick={handleConfirmDelete} style={{ background: '#FF6B6B' }}>
              <span>🗑️</span>
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairShopManagement;
